import { useState, type ReactNode } from 'react'
import { ChevronDown } from 'lucide-react'

type AccordionProps = {
  children: ReactNode
  className?: string
}

export function Accordion({ children, className = '' }: AccordionProps) {
  return <div className={`accordion ${className}`}>{children}</div>
}

type AccordionItemProps = {
  id: string
  title: ReactNode
  children: ReactNode
  isOpen?: boolean
  onToggle?: (isOpen: boolean) => void
}

export function AccordionItem({
  id,
  title,
  children,
  isOpen: controlledIsOpen,
  onToggle,
}: AccordionItemProps) {
  const [uncontrolledIsOpen, setUncontrolledIsOpen] = useState(false)

  const isControlled = controlledIsOpen !== undefined
  const isOpen = isControlled ? controlledIsOpen : uncontrolledIsOpen

  const handleToggle = () => {
    if (!isControlled) {
      setUncontrolledIsOpen(!isOpen)
    }
    onToggle?.(!isOpen)
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      handleToggle()
    } else if (e.key === 'Escape' && isOpen) {
      handleToggle()
    }
  }

  return (
    <div
      className={`accordion-item${isOpen ? ' is-open' : ''}`}
      style={{
        borderBottom: '1px solid var(--border, #e5e7eb)',
      }}
    >
      <button
        type="button"
        id={`accordion-btn-${id}`}
        aria-expanded={isOpen}
        aria-controls={`accordion-content-${id}`}
        onClick={handleToggle}
        onKeyDown={handleKeyDown}
        className="accordion-header"
        style={{
          width: '100%',
          textAlign: 'left',
          background: 'none',
          border: 'none',
          padding: '16px 0',
          cursor: 'pointer',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          fontSize: 15,
          fontWeight: 500,
          color: 'var(--text)',
        }}
      >
        <span>{title}</span>
        {/*
          .accordion-icon-Klasse defensiv behalten: kein CSS im Projekt
          targetet sie aktuell, aber sie dient als stabiler Hook fuer
          Tests (Accordion.test.tsx) und fuer etwaige zukuenftige
          theming-CSS-Regeln. Entfernen nur, wenn Tests umgestellt sind.
        */}
        <ChevronDown
          className="accordion-icon"
          aria-hidden="true"
          size={20}
          strokeWidth={2}
          style={{
            marginLeft: 12,
            flexShrink: 0,
            transition: 'transform 0.2s ease',
            transform: isOpen ? 'rotate(180deg)' : undefined,
          }}
        />
      </button>
      <div
        id={`accordion-content-${id}`}
        role="region"
        aria-labelledby={`accordion-btn-${id}`}
        aria-hidden={!isOpen}
        style={{
          overflow: 'hidden',
          maxHeight: isOpen ? '1000px' : '0',
          transition: 'max-height 0.25s ease',
          fontSize: 14,
          color: 'var(--text-subtle)',
          lineHeight: 1.6,
        }}
      >
        <div style={{ paddingBottom: 16 }}>{children}</div>
      </div>
    </div>
  )
}
